import { Show, SignInButton, SignUpButton } from '@clerk/react'
import { LayoutDashboard, Menu } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation } from '#router'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { StreakCounter } from '@/components/ui/streak-counter'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { useIsAdmin } from './admin-protected-route'
import ThemeToggle from './theme-toggle'
import { ThemedUserButton } from './themed-user-button'

export function MobileNav() {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const currentPath = `${location.pathname}${location.search}`
  const { isAdmin, isLoaded: isAdminLoaded } = useIsAdmin()
  const clientDashboard = useFeatureFlag('enable-client-dashboard')
  const navV2 = useFeatureFlag('enable-nav-v2')

  return (
    <header className="bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50 w-full border-b backdrop-blur pt-[env(safe-area-inset-top)]">
      <div className="flex h-14 items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <SheetHeader className="border-b p-4">
                <SheetTitle>Beyond CTCI</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col p-2">
                <Link
                  href="/problems"
                  className="rounded-md px-3 py-3 text-sm font-medium hover:bg-accent min-h-[44px]"
                  onClick={() => setOpen(false)}
                >
                  Problems
                </Link>
                <Show when="signed-in">
                  {navV2.isLoaded &&
                    navV2.enabled &&
                    clientDashboard.isLoaded &&
                    clientDashboard.enabled && (
                      <Link
                        href="/dashboard"
                        className="rounded-md px-3 py-3 text-sm font-medium hover:bg-accent min-h-[44px]"
                        onClick={() => setOpen(false)}
                      >
                        <span className="flex items-center gap-2">
                          <LayoutDashboard className="h-4 w-4" />
                          Dashboard
                        </span>
                      </Link>
                    )}
                  {isAdminLoaded && isAdmin && (
                    <Link
                      href="/admin"
                      className="rounded-md px-3 py-3 text-sm font-medium hover:bg-accent min-h-[44px]"
                      onClick={() => setOpen(false)}
                    >
                      Admin
                    </Link>
                  )}
                </Show>
                <div className="my-2 border-t" />
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-sm">Theme</span>
                  <ThemeToggle />
                </div>
              </nav>
              <div className="mt-auto border-t p-4">
                <Show when="signed-out">
                  <div className="flex flex-col gap-2">
                    <SignInButton
                      mode="modal"
                      forceRedirectUrl={currentPath}
                      fallbackRedirectUrl={currentPath}
                    >
                      <Button variant="outline" className="w-full">
                        Sign In
                      </Button>
                    </SignInButton>
                    <SignUpButton
                      mode="modal"
                      forceRedirectUrl={currentPath}
                      fallbackRedirectUrl={currentPath}
                    >
                      <Button className="w-full">Sign Up</Button>
                    </SignUpButton>
                  </div>
                </Show>
                <Show when="signed-in">
                  <div className="flex items-center justify-center">
                    <ThemedUserButton />
                  </div>
                </Show>
              </div>
            </SheetContent>
          </Sheet>
          <Link href="/" className="flex items-center gap-2">
            <img
              src="/icons/bctci.webp"
              alt=""
              aria-hidden="true"
              className="h-7 w-7"
            />
            <span className="text-base font-bold">Beyond CTCI</span>
          </Link>
        </div>
        <div className="flex items-center gap-1">
          <StreakCounter />
          <Show when="signed-in">
            <ThemedUserButton />
          </Show>
        </div>
      </div>
    </header>
  )
}
